import { FeatureDomain, MealType } from '@akin-home/amplify';
import { ChecklistModel } from '@akin-home/checklists';
import { useCommands } from '@akin-home/commands-react';
import { PhoneNumber } from '@akin-home/contacts';
import { Goal } from '@akin-home/goals';
import { Recipe } from '@akin-home/recipes';
import { isRobotUser } from '@akin-home/utils/native';
import { NavigatorScreenParams } from '@react-navigation/native';
import {
  NativeStackScreenProps,
  createNativeStackNavigator,
} from '@react-navigation/native-stack';
import { useCallback, useEffect } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useQueryClient } from 'react-query';
import { prefetchApps } from '../../app_library/api/queries';
import { CalendarItemDetailsScreen } from '../../calendar/components/CalendarItemDetailsScreen';
import { CalendarItemFormFocusableFields } from '../../calendar/components/CalendarItemForm/CalendarItemForm';
import CreateCalendarItemScreen from '../../calendar/components/CreateCalendarItemScreen';
import EditCalendarItemScreen from '../../calendar/components/EditCalendarItemScreen';
import { handleOpenCalendarItemCommand } from '../../chat/commands/calendarCommands/openCalendarItemCommand';
import { handleNavigationCommand } from '../../chat/commands/handleNavigationCommand';
import {
  ChatCommand,
  CommandTypes,
  NavigateCommand,
  OpenCalendarItemCommand,
} from '../../chat/commands/types';
import { CreateChecklistScreen } from '../../checklists/components/CreateChecklistScreen';
import { EditChecklistScreen } from '../../checklists/components/EditChecklistScreen';
import AlarmScreen from '../../components/Alarm/AlarmScreen';
import NotificationModal from '../../components/NotificationModal/NotificationModal';
import { CreateContactScreen } from '../../contacts/components/CreateContactScreen';
import { EditContactScreen } from '../../contacts/components/EditContactScreen';
import CreateGoalScreen from '../../goals/components/CreateGoalScreen/CreateGoalScreen';
import { EditGoalScreen } from '../../goals/components/EditGoalScreen';
import GoalStatisticsScreen from '../../goals/components/GoalStatisticsScreen';
import { useRefetchOnAppFocus } from '../../hooks/useRefetchOnAppFocus';
import { CalendarItemType } from '../../models/types';
import { ChooseHomeScreen } from '../../onboarding/components/ChooseHomeScreen/ChooseHomeScreen';
import { HomeScreenSetup } from '../../onboarding/components/HomeScreenSetup';
import RecipeDetailsScreen from '../../recipes/components/RecipeDetailsScreen';
import { RecipeSelectionScreen } from '../../recipes/components/RecipeSelectScreenView';
import { RobotSettingsScreen } from '../../robots/RobotsScreen/screens/RobotSettingsScreen';
import { BluetoothRobotConnectScreen } from '../../robots/bluetooth/BluetoothRobotConnectScreen/BluetoothRobotConnectScreen';
import { CreateRobotIdentityScreen } from '../../robots/identity/screens/CreateRobotIdentityScreen';
import { EditRobotIdentityScreen } from '../../robots/identity/screens/EditRobotIdentityScreen';
import { RobotIdentitiesScreen } from '../../robots/identity/screens/RobotIdentitiesScreen';
import { RobotIdentity } from '../../robots/identity/types';
import { RobotTeleopScreen } from '../../robots/teleop/RobotTeleopScreen';
import { RobotTutorialsScreen } from '../../robots/tutorials/RobotTutorialsScreen';
import { setBrightnessCommand } from '../../settings/brightness/commands/setBrightnessCommand';
import { SetBrightnessCommand } from '../../settings/brightness/commands/types';
import {
  OpenSystemSettingsCommand,
  openSystemSettingsCommand,
} from '../../settings/commands';
import SettingsNavigator from '../../settings/components/SettingsNavigator';
import { useStateStore } from '../../stores/useStateStore';
import { color } from '../../theme';
import { getCurrentUserID } from '../../users/functions';
import { CreateVideoCallScreenWrapper } from '../../video_call/screens/CreateVideoCallScreen';
import { VideoCallScreenWrapper } from '../../video_call/screens/VideoCallScreen';
import { DrawerNavigator, DrawerScreens } from '../MainDrawerNavigator';
import { createFocusEventHandler } from '../functions/createFocusEventHandler';
import { useRootStackNavigation } from '../hooks';

export type RootStackParamList = {
  /** The main tab navigator section */
  Main: NavigatorScreenParams<DrawerScreens>;

  RecipeSelect: {
    meal_id: string;
    meal_type: MealType;
  };

  /** Standalone version of the recipe form */
  RecipeForm: {
    recipe: Recipe;
    publicRecipe?: boolean;
    returnTo?: string;
    autoSave?: (recipe: Recipe) => void;
  };

  RecipeDetails: {
    /** ID to load details for */
    recipe_id: string;

    meal_id?: string;
  };

  CreateChecklist: { defaults?: Partial<ChecklistModel> } | undefined;

  EditChecklist: { id: string };

  EditContact: {
    contact_id: string;
  };

  CreateContact:
    | {
        defaults?: {
          name?: string;
          email?: string;
          phone?: PhoneNumber;
        };
      }
    | undefined;

  CalendarItemDetails: {
    id: string;
    type: CalendarItemType;
  };

  CreateCalendarItem: {
    type?: CalendarItemType;

    defaults?: {
      title?: string;
      start?: string;
      end?: string;
      emoji?: string;
      color?: string;
    };
  };

  EditCalendarItem: {
    /** The ID of the calendar item being modified */
    id: string;
    /** The type of calendar item */
    type: CalendarItemType;
    /** Optional recurrence ID. If a recurrence is being modified. */
    recurrence_id?: string;

    /** Field to auto focus on when the transition happens */
    focused_field?: CalendarItemFormFocusableFields;
  };

  CreateGoal: { defaults: Partial<Goal> } | undefined;
  EditGoal: { id: string };
  GoalStatistics: undefined;

  Settings: undefined;

  /** Opens the create video call screen for the provided contact */
  CreateVideoCall: {
    contact_id: string;
  };

  VideoCall: { url: string };

  Onboarding: undefined;

  /** Screen for modifying your dashboard */
  EditHomeScreen?: {
    show_back_button?: boolean;
  };

  /** Screen for connecting to Akin Robots */
  RobotConnect: undefined;

  /** Screen for tele-operating Robots */
  RobotTeleop: {
    robotId: string;
  };

  /** Screen for changing settings of Robots */
  RobotSettings: {
    robotId: string;
  };

  /** Screen for configuring Identities known by a Robot */
  RobotIdentities: {
    robotId: string;
  };

  CreateRobotIdentity: {
    robotId: string;
    defaults?: Partial<RobotIdentity>;
  };

  EditRobotIdentity: {
    robotId: string;
    identity_id: string;
  };

  RobotTutorials: {
    robotId: string;
  };
};

export type RootStackScreenProps<T extends keyof RootStackParamList> =
  NativeStackScreenProps<RootStackParamList, T>;

const RootStack = createNativeStackNavigator<RootStackParamList>();

export const RootStackNavigator = () => {
  const query_client = useQueryClient();
  useRefetchOnAppFocus();

  // If the navigation prop needs to be passed deeply, refactor to use this method instead
  // https://reactnavigation.org/docs/navigating-without-navigation-prop/
  const navigation = useRootStackNavigation();
  const { navigate, goBack, canGoBack } = navigation;

  const activeAlarms = useStateStore((state) => state.active_alarms);
  const activeNotifications = useStateStore(
    (state) => state.active_notifications
  );

  useCommands(
    [
      CommandTypes.NAVIGATE,
      CommandTypes.TIMER_SET,
      CommandTypes.TIMER_PAUSE,
      CommandTypes.TIMER_STOP,
      CommandTypes.TIMER_RESUME,
      CommandTypes.TIMER_RESET,
      CommandTypes.SET_BRIGHTNESS,
      CommandTypes.OPEN_SYSTEM_SETTINGS,
      CommandTypes.GO_BACK,
      CommandTypes.OPEN_CALENDAR_ITEM,
    ],
    useCallback(
      (commands: ChatCommand[]) => {
        commands.forEach((cmd) => {
          switch (cmd.type as CommandTypes) {
            case CommandTypes.NAVIGATE:
              handleNavigationCommand(
                cmd as unknown as NavigateCommand,
                navigate
              );
              break;
            case CommandTypes.GO_BACK:
              goBack();
              break;
            // case CommandTypes.TIMER_SET: {
            //   const { unit, value } = cmd.data;
            //   const timer: Timer = {
            //     type: 'Timer',
            //     unit,
            //     value,
            //   };
            // setTimerState('Running');
            // setActiveTimer(timer);
            //   break;
            // }
            // case CommandTypes.TIMER_PAUSE:
            //   setTimerState('Paused');
            //   break;
            // case CommandTypes.TIMER_STOP:
            //   setTimerState('Paused');
            //   onDismissTimer();
            //   break;
            // case CommandTypes.TIMER_RESUME:
            //   setTimerState('Running');
            //   break;
            // case CommandTypes.TIMER_RESET:
            //   setTimerState('Reset');
            //   onDismissTimer();
            //   setActiveTimer(activeTimer);
            //   break;
            case CommandTypes.SET_BRIGHTNESS:
              setBrightnessCommand({
                command: cmd as unknown as SetBrightnessCommand,
                query_client,
              });
              break;
            case CommandTypes.OPEN_SYSTEM_SETTINGS:
              openSystemSettingsCommand({
                command: cmd as unknown as OpenSystemSettingsCommand,
              });
              break;
            case CommandTypes.OPEN_CALENDAR_ITEM:
              handleOpenCalendarItemCommand({
                command: cmd as unknown as OpenCalendarItemCommand,
                navigation,
              });
              break;
            default:
              // Handle unknown command type
              break;
          }
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [query_client, navigate, goBack, canGoBack]
    )
  );

  useEffect(() => {
    if (Platform.OS !== 'web') {
      prefetchApps(query_client);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={styles.container}>
      <RootStack.Navigator
        screenOptions={screenOptions}
        initialRouteName="Main"
      >
        <RootStack.Screen name="Main" component={DrawerNavigator} />

        <RootStack.Screen
          name="RecipeSelect"
          component={RecipeSelectionScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.MEALS,
            }),
          })}
        />

        <RootStack.Screen
          name="RecipeDetails"
          component={RecipeDetailsScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.MEALS,
              record_id: route.params.recipe_id,
            }),
          })}
        />

        <RootStack.Screen
          name="CreateContact"
          component={CreateContactScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.CONTACTS,
            }),
          })}
        />

        <RootStack.Screen
          name="EditContact"
          component={EditContactScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.CONTACTS,
              record_id: route.params.contact_id,
            }),
          })}
        />

        <RootStack.Screen
          name="CreateChecklist"
          component={CreateChecklistScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.CHECKLISTS,
            }),
          })}
        />

        <RootStack.Screen
          name="EditChecklist"
          component={EditChecklistScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.CHECKLISTS,
              record_id: route.params.id,
            }),
          })}
        />

        <RootStack.Screen
          name="CreateCalendarItem"
          component={CreateCalendarItemScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.CALENDAR,
            }),
          })}
        />

        <RootStack.Screen
          name="EditCalendarItem"
          component={EditCalendarItemScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.CALENDAR,
              record_id: route.params.id,
            }),
          })}
        />

        <RootStack.Screen
          name="CalendarItemDetails"
          component={CalendarItemDetailsScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.CALENDAR,
              record_id: route.params.id,
            }),
          })}
        />

        <RootStack.Screen
          name="CreateGoal"
          component={CreateGoalScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.GOALS,
            }),
          })}
        />

        <RootStack.Screen
          name="EditGoal"
          component={EditGoalScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.GOALS,
              record_id: route.params.id,
            }),
          })}
        />

        <RootStack.Screen
          name="GoalStatistics"
          component={GoalStatisticsScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
              feature_domain: FeatureDomain.GOALS,
            }),
          })}
        />

        <RootStack.Screen
          name="CreateVideoCall"
          component={CreateVideoCallScreenWrapper}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
            }),
          })}
        />

        <RootStack.Screen
          name="VideoCall"
          component={VideoCallScreenWrapper}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
            }),
          })}
        />

        <RootStack.Screen name="Settings" component={SettingsNavigator} />

        <RootStack.Screen
          name="EditHomeScreen"
          component={HomeScreenSetup}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
            }),
          })}
        />

        <RootStack.Screen
          name="Onboarding"
          component={ChooseHomeScreen}
          listeners={({ route }) => ({
            focus: onFocusEvent({
              page: route.name,
            }),
          })}
        />

        {isRobotUser() ? (
          <RootStack.Screen
            name="RobotConnect"
            component={BluetoothRobotConnectScreen}
            listeners={({ route }) => ({
              focus: onFocusEvent({
                page: route.name,
              }),
            })}
          />
        ) : null}

        {isRobotUser() ? (
          <RootStack.Screen
            name="RobotTeleop"
            component={RobotTeleopScreen}
            listeners={({ route }) => ({
              focus: onFocusEvent({
                page: route.name,
              }),
            })}
          />
        ) : null}

        {isRobotUser() ? (
          <RootStack.Screen
            name="RobotSettings"
            component={RobotSettingsScreen}
            listeners={({ route }) => ({
              focus: onFocusEvent({
                page: route.name,
              }),
            })}
          />
        ) : null}

        {isRobotUser() ? (
          <RootStack.Screen
            name="RobotIdentities"
            component={RobotIdentitiesScreen}
            listeners={({ route }) => ({
              focus: onFocusEvent({
                page: route.name,
              }),
            })}
          />
        ) : null}

        {isRobotUser() ? (
          <RootStack.Screen
            name="CreateRobotIdentity"
            component={CreateRobotIdentityScreen}
            listeners={({ route }) => ({
              focus: onFocusEvent({
                page: route.name,
              }),
            })}
          />
        ) : null}

        {isRobotUser() ? (
          <RootStack.Screen
            name="EditRobotIdentity"
            component={EditRobotIdentityScreen}
            listeners={({ route }) => ({
              focus: onFocusEvent({
                page: route.name,
                record_id: route.params.identity_id,
              }),
            })}
          />
        ) : null}

        {isRobotUser() ? (
          <RootStack.Screen
            name="RobotTutorials"
            component={RobotTutorialsScreen}
            listeners={({ route }) => ({
              focus: onFocusEvent({
                page: route.name,
              }),
            })}
          />
        ) : null}
      </RootStack.Navigator>

      {activeNotifications?.length ? (
        <NotificationModal items={activeNotifications} />
      ) : null}
      {activeAlarms?.length ? <AlarmScreen alarms={activeAlarms} /> : null}

      {/* {activeTimer && (
        <TimerModal
          onDismiss={onDismissTimer}
          timerState={timerState}
          item={activeTimer}
          updateTimerState={setTimerState}
        />
      )} */}
    </View>
  );
};

const screenOptions = { headerShown: false };

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    backgroundColor: color.secondary,
  },
});

const onFocusEvent = createFocusEventHandler({
  getUserID: getCurrentUserID,
});
